import React, { useEffect, useRef, useState } from 'react';
import {
   ToastList,
   useViewForm,
} from '@devesharp/react/web';
import * as Yup from 'yup';
import dateFNS from 'date-fns';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { tap } from 'rxjs/operators';
import {
   CompanyCreateResolve,
   CompanyGetResolve,
   CompanyUpdateResolve,
} from '~/api';
import { states } from '~/services/data';
import { OnlyNumbers, searchCEP } from '~/services';
import history from '~/config/history';
import { companySelectState } from '~/recoil/company-select.state';

const yupValidateSchema = Yup.object().shape({
   type: Yup.string().nullable().required('Este campo é obrigatório'),
   first_name: Yup.string().required('Este campo é obrigatório'),
   last_name: Yup.string().required('Este campo é obrigatório'),
   email: Yup.string().required('Este campo é obrigatório'),
   phone_number: Yup.string().required('Este campo é obrigatório'),
   taxpayer_id: Yup.string().required('Este campo é obrigatório'),
   birthdate: Yup.string().required('Este campo é obrigatório'),
   address_line1: Yup.string().required('Este campo é obrigatório'),
   address_line2: Yup.string().required('Este campo é obrigatório'),
   address_neighborhood: Yup.string().required('Este campo é obrigatório'),
   address_city: Yup.string().required('Este campo é obrigatório'),
   address_state: Yup.string().required('Este campo é obrigatório'),
   address_postal_code: Yup.string().required('Este campo é obrigatório'),
});

const yupValidateBusnissSchema = Yup.object().shape({
   type: Yup.string().nullable().required('Este campo é obrigatório'),
   owner_first_name: Yup.string().required('Este campo é obrigatório'),
   owner_last_name: Yup.string().required('Este campo é obrigatório'),
   owner_email: Yup.string().required('Este campo é obrigatório'),
   owner_phone_number: Yup.string().required('Este campo é obrigatório'),
   owner_taxpayer_id: Yup.string().required('Este campo é obrigatório'),
   owner_birthdate: Yup.string().required('Este campo é obrigatório'),
   business_name: Yup.string().required('Este campo é obrigatório'),
   business_phone: Yup.string().required('Este campo é obrigatório'),
   business_email: Yup.string().required('Este campo é obrigatório'),
   business_opening_date: Yup.string().test('date', 'Data inválida', (value) => {
      if (!value) return true;

      return dateFNS.isValid(new Date(value as string));
   }),
   ein: Yup.string().required('Este campo é obrigatório'),
   business_address_line1: Yup.string().required('Este campo é obrigatório'),
   business_address_line2: Yup.string().required('Este campo é obrigatório'),
   business_address_neighborhood: Yup.string().required('Este campo é obrigatório'),
   business_address_city: Yup.string().required('Este campo é obrigatório'),
   business_address_state: Yup.string().nullable().required('Este campo é obrigatório'),
   business_address_postal_code: Yup.string().required('Este campo é obrigatório'),
   owner_address_line1: Yup.string().required('Este campo é obrigatório'),
   owner_address_line2: Yup.string().required('Este campo é obrigatório'),
   owner_address_neighborhood: Yup.string().required('Este campo é obrigatório'),
   owner_address_city: Yup.string().required('Este campo é obrigatório'),
   owner_address_state: Yup.string().nullable().required('Este campo é obrigatório'),
   owner_address_postal_code: Yup.string().required('Este campo é obrigatório'),
});

export function useEstablishmentCreatePage(): any {
   const { id } = useParams<any>();

   document.title = id
      ? 'Editar estabelecimento - XPAG Tecnologia'
      : 'Novo estabelecimento - XPAG Tecnologia';
   const [isLoadingCep, setIsLoadingCep] = useState(false);
   const ref = useRef('');
   const initialData = {
      type: 'business',
   };

   const [personType, setPersonType] = useState('business');
   const createdCompanyId = useRef<number | string | null>(null);

   const {
      starting,
      saving,
      formRef,
      submit,
      errorLoadData,
      onSubmitForm,
      reloadPage,
      registerOnInit,
   } = useViewForm({
      id,
      yupValidateSchema: personType == 'individual' ? yupValidateSchema : yupValidateBusnissSchema,
      resolves: {},
      resolveGet: CompanyGetResolve,
      resolveCreate: (data) =>
         CompanyCreateResolve(data).pipe(
            tap((result) => {
               createdCompanyId.current = result?.id ?? null;
            }),
         ),
      resolveUpdate: (params) => CompanyUpdateResolve(id, params),
      onFailed(e, m) {
         ToastList.show({
            message: m.data?.error ?? m.data?.data?.error ?? 'Erro ao salvar estabelecimento',
            type: 'error',
         });
      },
      handleFormData(data) {
         const newData = { ...data };
         if (newData.business_opening_date) {
            const [day, month, year] = newData.business_opening_date.split('/');
            newData.business_opening_date = `${year}-${month}-${day}`;
         }
         return newData;
      },
      onSuccess(creating) {
         if (creating) {
            ToastList.show({
               message: 'Estabelecimento cadastrado. Complete o cadastro na Zoop.',
               type: 'success',
            });
            history.push(
               createdCompanyId.current
                  ? `/establishment/${createdCompanyId.current}/zoop`
                  : '/establishment',
            );
            return;
         }

         ToastList.show({
            message: 'Estabelecimento editado com sucesso',
            type: 'success',
         });
         history.push('/establishment');
      },
   });

   const [sellerSelect, setSellerSelect] = useRecoilState(companySelectState);

   const [resource, setResource] = useState<any>();

   function changePersonType() {
      setPersonType(formRef.current.getFieldValue('type'));
   }

   registerOnInit((resources: any) => {
      if (resources.resource) {
         const resource2 = { ...resources.resource };

         setResource(resource2);
         setPersonType(resource2.type ?? 'business');
         formRef.current.setData(resource2);
      }
   });

   const formInfoData = {
      person_type: [
         {
            value: 'individual',
            name: 'Pessoa física',
         },
         {
            value: 'business',
            name: 'Pessoa jurídica',
         },
      ],
      states: states.map((state) => ({ name: state.name, value: state.id })),
   };

   function onChangeCep(fieldPrefix = 'address'): any {
      const cep = OnlyNumbers(formRef.current.getFieldValue(`${fieldPrefix}_postal_code`));
      if (cep.length == 8 && ref.current !== cep) {
         setIsLoadingCep(true);
         formRef.current.setFieldError(`${fieldPrefix}_postal_code`, '');
         searchCEP(cep)
            .then((e: any) => {
               setIsLoadingCep(false);
               formRef.current.setData({
                  ...formRef.current.getData(),
                  [`${fieldPrefix}_line1`]: e.data.street,
                  [`${fieldPrefix}_city`]: e.data.city,
                  [`${fieldPrefix}_neighborhood`]: e.data.neighborhood,
                  [`${fieldPrefix}_state`]: e.data.state,
               });
            })
            .catch(() => {
               setIsLoadingCep(false);
               formRef.current.setFieldError(`${fieldPrefix}_postal_code`, 'CEP não encontrado');
            });
      }
      ref.current = cep;
   }

   return {
      onSubmit: onSubmitForm,
      id,
      editing: !!id,
      personType,
      changePersonType,
      formRef,
      reloadPage,
      starting,
      saving,
      isLoadingCep,
      onChangeCep,
      errorLoadData,
      formInfoData,
      sellerSelect,
      initialData,
      submit,
   };
}
